import React, { useState } from 'react';
import { 
  Users, 
  UserPlus, 
  KeyRound, 
  ShieldCheck, 
  ShieldAlert, 
  Copy, 
  Check, 
  RotateCcw, 
  Lock, 
  AlertTriangle, 
  Sparkles, 
  Power, 
  Activity, 
  Sliders, 
  Clock,
  MoreVertical,
  Mail,
  Zap,
  CheckCircle2,
  Trash2,
  Settings,
  Gift,
  Bot,
  BellRing,
  Coins,
  Globe,
  RefreshCw,
  Smartphone,
  Edit3
} from 'lucide-react';
import { StatusBadge } from '../components/common/StatusBadge';
import { Modal } from '../components/common/Modal';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import { AdminMember, AdminRole, GovernanceConfig } from '../types/admin';
import { useAuth } from '../context/AuthContext';

export const AdminManagementView: React.FC = () => {
  const { currentUser, updateProfile } = useAuth();
  const isSuperAdmin = currentUser?.role === 'super_admin';

  const [activeTab, setActiveTab] = useState<'team' | 'governance'>('team');

  // Admin Team Members state
  const [admins, setAdmins] = useState<AdminMember[]>([
    {
      uid: 'adm_001_sammy',
      name: 'Sammy Oliver',
      displayName: 'Sammy Oliver',
      email: 'oliversammytwist@gmail.com',
      role: 'super_admin',
      isSuperAdmin: true,
      twoFactorEnabled: true,
      status: 'active',
      lastLoginAt: 'Today at 02:45 AM',
      createdAt: '2026-01-01',
      avatarUrl: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=120&h=120&fit=crop&crop=faces'
    },
    {
      uid: 'adm_002_tola',
      name: 'Tola Adebayo',
      displayName: 'Tola Adebayo',
      email: 'tola.adebayo@reuseit.shop',
      role: 'mediator',
      isSuperAdmin: false,
      twoFactorEnabled: true,
      status: 'active',
      lastLoginAt: 'Yesterday at 04:10 PM',
      createdAt: '2026-03-15'
    },
    {
      uid: 'adm_003_grace',
      name: 'Grace Nwosu',
      displayName: 'Grace Nwosu',
      email: 'grace.nwosu@reuseit.shop',
      role: 'content_editor',
      isSuperAdmin: false,
      twoFactorEnabled: false,
      status: 'active',
      lastLoginAt: '3 days ago',
      createdAt: '2026-04-20'
    },
    {
      uid: 'adm_004_ibrahim',
      name: 'Ibrahim Musa',
      displayName: 'Ibrahim Musa',
      email: 'ibrahim.m@reuseit.shop',
      role: 'support_agent',
      isSuperAdmin: false,
      twoFactorEnabled: false,
      status: 'active',
      lastLoginAt: '5 days ago',
      createdAt: '2026-06-01'
    }
  ]);

  // Governance Settings State
  const [governance, setGovernance] = useState<GovernanceConfig>({
    baseEscrowFeeMin: 500,
    tierLowPercent: 3.25,
    tierHighPercent: 1.25,
    platformFeePercent: 7.5,
    vatOnPlatformFeePercent: 7.5,
    minSupportedAppVersion: '1.4.0',
    maintenanceMode: false,
    maintenanceMessage: 'ReUseIt is undergoing scheduled system upgrades. Trading and payments will resume shortly.',
    flutterwaveFloatBalance: 2450000,
    flutterwaveFloatThreshold: 500000,
    // Viral Referral Program
    referralRewardNgn: 100,
    refereeDiscountNgn: 100,
    referralEcoPoints: 50,
    referralProgramActive: true,
    // Automated AI Content Moderation
    aiContentModerationEnabled: true,
    aiConfidenceQuarantineThreshold: 85,
    autoFlagReplicaKeywords: true,
    // Treasury DLQ & Alerts
    lowBalanceAlertPhone: '+234 801 234 5678',
    lowBalanceAlertEmail: 'treasury@reuseit.shop',
    failedPayoutCount: 2,
    // Fraud Velocity Limits
    maxListingsPerHourPerUser: 20,
    bankChangeWithdrawalFreezeHours: 24,
    geofenceSpoofingCheckEnabled: true,
  });

  const [governanceSaved, setGovernanceSaved] = useState(false);
  const [retryingPayouts, setRetryingPayouts] = useState(false);
  const [payoutsRetriedSuccess, setPayoutsRetriedSuccess] = useState(false);

  const handleRetryFailedPayouts = async () => {
    setRetryingPayouts(true);
    await new Promise(r => setTimeout(r, 1200));
    setGovernance(prev => ({ ...prev, failedPayoutCount: 0 }));
    setRetryingPayouts(false);
    setPayoutsRetriedSuccess(true);
    setTimeout(() => setPayoutsRetriedSuccess(false), 3000);
  };

  // Add Admin Modal State
  const [addAdminModalOpen, setAddAdminModalOpen] = useState(false);
  const [newAdminName, setNewAdminName] = useState('');
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminRole, setNewAdminRole] = useState<AdminRole>('mediator');
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [copiedCredential, setCopiedCredential] = useState(false);
  const [createdAdminReceipt, setCreatedAdminReceipt] = useState<{ email: string; pass: string; role: string } | null>(null);

  // Reset Credentials Modal State
  const [resetModalAdmin, setResetModalAdmin] = useState<AdminMember | null>(null);
  const [resetPasswordGenerated, setResetPasswordGenerated] = useState('');
  const [resetCopied, setResetCopied] = useState(false);

  // Edit Admin & Change Email Modal State
  const [editAdminModalOpen, setEditAdminModalOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<AdminMember | null>(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editRole, setEditRole] = useState<AdminRole>('mediator');
  const [editStatus, setEditStatus] = useState<'active' | 'suspended'>('active');
  const [edit2fa, setEdit2fa] = useState(false);
  const [adminUpdateSuccess, setAdminUpdateSuccess] = useState('');

  const handleOpenEditAdmin = (admin: AdminMember) => {
    setEditingAdmin(admin);
    setEditName(admin.displayName || admin.name || '');
    setEditEmail(admin.email || '');
    setEditRole(admin.role);
    setEditStatus(admin.status);
    setEdit2fa(Boolean(admin.twoFactorEnabled));
    setEditAdminModalOpen(true);
  };

  const handleSaveEditAdmin = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingAdmin || !editEmail.trim() || !editName.trim()) return;

    const previousEmail = editingAdmin.email;
    const newEmail = editEmail.trim();

    setAdmins(prev => prev.map(a => {
      if (a.uid === editingAdmin.uid) {
        return {
          ...a,
          name: editName.trim(),
          displayName: editName.trim(),
          email: newEmail,
          role: editRole,
          isSuperAdmin: editRole === 'super_admin',
          status: editStatus,
          twoFactorEnabled: edit2fa
        };
      }
      return a;
    }));

    // If the super admin updated their own profile, sync AuthContext
    if (currentUser && (currentUser.email === previousEmail || currentUser.uid === editingAdmin.uid)) {
      updateProfile({
        displayName: editName.trim(),
        email: newEmail
      });
    }

    setEditAdminModalOpen(false);
    setAdminUpdateSuccess(`Administrator "${editName}" details & login email updated to ${newEmail}!`);
    setTimeout(() => setAdminUpdateSuccess(''), 4500);
  };

  // Cryptographically secure password generator
  const generateSecurePassword = () => {
    const charset = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*';
    let result = 'ReUseIt!';
    const randomValues = new Uint32Array(8);
    window.crypto.getRandomValues(randomValues);
    for (let i = 0; i < 8; i++) {
      result += charset[randomValues[i] % charset.length];
    }
    return result;
  };

  const handleOpenAddAdmin = () => {
    setNewAdminName('');
    setNewAdminEmail('');
    setNewAdminRole('mediator');
    setGeneratedPassword(generateSecurePassword());
    setAddAdminModalOpen(true);
    setCreatedAdminReceipt(null);
  };

  const handleCreateAdmin = () => {
    if (!newAdminName.trim() || !newAdminEmail.trim()) return;

    const newMember: AdminMember = {
      uid: `adm_${Date.now()}`,
      name: newAdminName,
      displayName: newAdminName,
      email: newAdminEmail,
      role: newAdminRole,
      isSuperAdmin: newAdminRole === 'super_admin',
      twoFactorEnabled: false,
      status: 'active',
      createdAt: 'Just now',
      tempPassword: generatedPassword
    };

    setAdmins([...admins, newMember]);
    setCreatedAdminReceipt({
      email: newAdminEmail,
      pass: generatedPassword,
      role: newAdminRole
    });
  };

  const handleOpenResetCredentials = (admin: AdminMember) => {
    const newPass = generateSecurePassword();
    setResetModalAdmin(admin);
    setResetPasswordGenerated(newPass);
    setResetCopied(false);
  };

  const handleConfirmResetCredentials = () => {
    if (!resetModalAdmin) return;
    setAdmins(prev => prev.map(a => {
      if (a.uid === resetModalAdmin.uid) {
        return { ...a, tempPassword: resetPasswordGenerated };
      }
      return a;
    }));
  };

  const handleToggleSuspend = (uid: string) => {
    setAdmins(prev => prev.map(a => {
      if (a.uid === uid) {
        return { ...a, status: a.status === 'active' ? 'suspended' : 'active' };
      }
      return a;
    }));
  };

  const handleSaveGovernance = (e: React.FormEvent) => {
    e.preventDefault();
    setGovernanceSaved(true);
    setTimeout(() => setGovernanceSaved(false), 3000);
  };

  return (
    <div className="space-y-6">
      {/* Header & Tabs */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-[#22493c] dark:border-[#22493c] light:border-[#D5E4DC] pb-4">
        <div>
          <h3 className="text-lg font-bold text-white dark:text-white light:text-[#0D1C17]">
            Admin Team & Mobile App Governance Console
          </h3>
          <p className="text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
            Manage administrative staff credentials, 2FA security enforcement, and live mobile app operational parameters
          </p>
        </div>

        <div className="flex items-center gap-3">
          {isSuperAdmin ? (
            <button
              onClick={handleOpenAddAdmin}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold text-forest-void bg-brand-500 hover:bg-brand-400 shadow-glow-sm transition-all"
            >
              <UserPlus className="w-4 h-4" />
              <span>Add Admin Member</span>
            </button>
          ) : (
            <span className="text-xs px-3.5 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/25 text-amber-300 flex items-center gap-1.5 font-medium">
              <Lock className="w-3.5 h-3.5 text-amber-400" />
              <span>Super Admin Jurisdiction Only</span>
            </span>
          )}
        </div>
      </div>

      {/* 2-Tab Switcher */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => setActiveTab('team')}
          className={`px-5 py-2.5 rounded-full text-xs font-bold transition-all flex items-center gap-2
            ${activeTab === 'team'
              ? 'bg-brand-500 text-forest-void shadow-glow-sm'
              : 'text-[#90cbb7] hover:text-white dark:text-[#90cbb7] dark:hover:text-white light:text-[#527063] light:hover:text-[#0D1C17]'
            }
          `}
        >
          <Users className="w-3.5 h-3.5" />
          <span>Admin Staff Directory ({admins.length})</span>
        </button>

        <button
          onClick={() => setActiveTab('governance')}
          className={`px-5 py-2.5 rounded-full text-xs font-bold transition-all flex items-center gap-2
            ${activeTab === 'governance'
              ? 'bg-brand-500 text-forest-void shadow-glow-sm'
              : 'text-[#90cbb7] hover:text-white dark:text-[#90cbb7] dark:hover:text-white light:text-[#527063] light:hover:text-[#0D1C17]'
            }
          `}
        >
          <Sliders className="w-3.5 h-3.5" />
          <span>App Governance & Fee Engine</span>
          {governance.maintenanceMode && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-rose-500 text-white">
              Kill-Switch Active
            </span>
          )}
        </button>
      </div>

      {/* TAB 1: ADMIN STAFF DIRECTORY */}
      {activeTab === 'team' && (
        <div className="space-y-4">
          {adminUpdateSuccess && (
            <div className="p-4 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs flex items-center justify-between gap-2 shadow-glow-sm">
              <div className="flex items-center gap-2.5">
                <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-400" />
                <span>{adminUpdateSuccess}</span>
              </div>
              <button
                type="button"
                onClick={() => setAdminUpdateSuccess('')}
                className="text-xs text-[#90cbb7] hover:text-white"
              >
                ✕
              </button>
            </div>
          )}

          <div className="p-4 rounded-2xl bg-brand-500/10 border border-brand-500/20 text-brand-300 text-xs flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 shrink-0 text-brand-400" />
            <span>
              <strong>Role-Based Access Control (RBAC):</strong> Super Admins possess full system jurisdiction including financial overrides, staff onboarding, and account purging. Other roles have strictly bounded permissions (Mediators triage disputes, Content Editors manage CMS, Support Agents handle tickets).
            </span>
          </div>

          <div className="rounded-3xl border overflow-hidden transition-colors
            bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c]
            light:bg-white light:border-[#D5E4DC] light:shadow-sm
          ">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="border-b border-[#22493c] dark:border-[#22493c] light:border-[#EBF2EE] text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] uppercase tracking-wider font-semibold">
                    <th className="py-4 px-6">Admin Member</th>
                    <th className="py-4 px-6">Designated Role</th>
                    <th className="py-4 px-6">2FA Security</th>
                    <th className="py-4 px-6">Account Status</th>
                    <th className="py-4 px-6">Last Active</th>
                    <th className="py-4 px-6 text-right">Administrative Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#22493c] dark:divide-[#22493c] light:divide-[#EBF2EE]">
                  {admins.map((adm) => (
                    <tr key={adm.uid} className="hover:bg-[#1a382e]/50 dark:hover:bg-[#1a382e]/50 light:hover:bg-gray-50/80 transition-colors">
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-brand-500/20 border border-brand-500/30 text-brand-400 flex items-center justify-center font-bold text-sm">
                            {adm.displayName.charAt(0)}
                          </div>
                          <div>
                            <div className="font-bold text-sm text-white dark:text-white light:text-[#0D1C17]">
                              {adm.displayName}
                            </div>
                            <div className="text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                              {adm.email}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="py-4 px-6">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider border
                          ${adm.role === 'super_admin' ? 'bg-brand-500/20 text-brand-400 border-brand-500/30' :
                            adm.role === 'mediator' ? 'bg-purple-500/20 text-purple-300 border-purple-500/30' :
                            adm.role === 'content_editor' ? 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30' :
                            'bg-blue-500/20 text-blue-300 border-blue-500/30'}
                        `}>
                          {adm.role.replace('_', ' ')}
                        </span>
                      </td>

                      <td className="py-4 px-6">
                        {adm.twoFactorEnabled ? (
                          <span className="flex items-center gap-1.5 text-emerald-400 font-semibold">
                            <ShieldCheck className="w-4 h-4" />
                            Enabled
                          </span>
                        ) : (
                          <span className="flex items-center gap-1.5 text-amber-400 font-semibold">
                            <ShieldAlert className="w-4 h-4" />
                            Disabled
                          </span>
                        )}
                      </td>

                      <td className="py-4 px-6">
                        <StatusBadge status={adm.status} />
                      </td>

                      <td className="py-4 px-6 text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                        {adm.lastLoginAt || 'Never'}
                      </td>

                      <td className="py-4 px-6 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {isSuperAdmin && (
                            <button
                              onClick={() => handleOpenEditAdmin(adm)}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border border-brand-500/30 bg-brand-500/10 text-brand-400 hover:bg-brand-500/20 transition-colors"
                              title="Update staff details & change login email"
                            >
                              <Edit3 className="w-3.5 h-3.5" />
                              <span>Edit & Change Email</span>
                            </button>
                          )}

                          <button
                            onClick={() => handleOpenResetCredentials(adm)}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-[#22493c] text-white hover:bg-[#2a5547] transition-colors"
                            title="Generate fresh login password for this admin"
                          >
                            <KeyRound className="w-3.5 h-3.5 text-brand-400" />
                            <span>Reset Credentials</span>
                          </button>

                          {isSuperAdmin && adm.role !== 'super_admin' && (
                            <button
                              onClick={() => handleToggleSuspend(adm.uid)}
                              className={`p-1.5 rounded-lg transition-colors ${adm.status === 'active' ? 'text-amber-400 hover:bg-amber-500/15' : 'text-emerald-400 hover:bg-emerald-500/15'}`}
                              title={adm.status === 'active' ? 'Suspend Admin Access' : 'Reactivate Access'}
                            >
                              <Power className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: MOBILE APP GOVERNANCE & FEE ENGINE */}
      {activeTab === 'governance' && (
        <form onSubmit={handleSaveGovernance} className="space-y-6">
          {governanceSaved && (
            <div className="p-4 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              <span>Mobile app operational parameters updated successfully! Synchronized to Firestore.</span>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Dynamic Fee & VAT Engine */}
            <div className="rounded-3xl p-6 sm:p-8 border transition-colors space-y-4
              bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c]
              light:bg-white light:border-[#D5E4DC] light:shadow-sm
            ">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-2 rounded-xl bg-brand-500/15 text-brand-400">
                  <Sliders className="w-4 h-4" />
                </div>
                <h4 className="text-base font-bold text-white dark:text-white light:text-[#0D1C17]">
                  Dynamic Escrow & Payout Fee Engine
                </h4>
              </div>
              <p className="text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                Configure marketplace take-rate and legal tax parameters applied on escrow transactions and vendor payouts.
              </p>

              <div className="space-y-3.5 text-xs pt-2">
                <div>
                  <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1">
                    Minimum Base Escrow Service Fee (NGN):
                  </label>
                  <input
                    type="number"
                    value={governance.baseEscrowFeeMin}
                    onChange={(e) => setGovernance({ ...governance, baseEscrowFeeMin: Number(e.target.value) })}
                    className="w-full p-2.5 rounded-xl text-xs outline-none border transition-colors
                      bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                      light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17] focus:border-brand-500"
                  />
                  <span className="text-[10px] text-[#90cbb7]/70">Absolute minimum base fee charged to buyers.</span>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1">
                      Tier 1 Rate (Low Cost Items):
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.05"
                        value={governance.tierLowPercent}
                        onChange={(e) => setGovernance({ ...governance, tierLowPercent: Number(e.target.value) })}
                        className="w-full p-2.5 rounded-xl text-xs outline-none border transition-colors
                          bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                          light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17] focus:border-brand-500"
                      />
                      <span className="absolute right-3 top-2.5 text-[#90cbb7]">%</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1">
                      Tier 2 Rate (High Value Items):
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.05"
                        value={governance.tierHighPercent}
                        onChange={(e) => setGovernance({ ...governance, tierHighPercent: Number(e.target.value) })}
                        className="w-full p-2.5 rounded-xl text-xs outline-none border transition-colors
                          bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                          light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17] focus:border-brand-500"
                      />
                      <span className="absolute right-3 top-2.5 text-[#90cbb7]">%</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1">
                      Vendor Payout Fee (Platform):
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.1"
                        value={governance.platformFeePercent}
                        onChange={(e) => setGovernance({ ...governance, platformFeePercent: Number(e.target.value) })}
                        className="w-full p-2.5 rounded-xl text-xs outline-none border transition-colors
                          bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                          light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17] focus:border-brand-500"
                      />
                      <span className="absolute right-3 top-2.5 text-[#90cbb7]">%</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1">
                      VAT on Platform Fee:
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.1"
                        value={governance.vatOnPlatformFeePercent}
                        onChange={(e) => setGovernance({ ...governance, vatOnPlatformFeePercent: Number(e.target.value) })}
                        className="w-full p-2.5 rounded-xl text-xs outline-none border transition-colors
                          bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                          light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17] focus:border-brand-500"
                      />
                      <span className="absolute right-3 top-2.5 text-[#90cbb7]">%</span>
                    </div>
                  </div>
                </div>

                <div className="p-3 rounded-xl bg-[#10231d] dark:bg-[#10231d] border border-[#22493c] text-[11px] text-brand-400">
                  Total Vendor Settlement Net Deduction: <strong>8.0625%</strong> ({governance.platformFeePercent}% + {(governance.platformFeePercent * governance.vatOnPlatformFeePercent / 100).toFixed(4)}% VAT)
                </div>
              </div>
            </div>

            {/* Emergency Kill-Switch & App Version Enforcer */}
            <div className="rounded-3xl p-6 sm:p-8 border transition-colors space-y-4
              bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c]
              light:bg-white light:border-[#D5E4DC] light:shadow-sm
            ">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-2 rounded-xl bg-rose-500/15 text-rose-400">
                  <Power className="w-4 h-4" />
                </div>
                <h4 className="text-base font-bold text-white dark:text-white light:text-[#0D1C17]">
                  Emergency Kill-Switch & Version Enforcer
                </h4>
              </div>
              <p className="text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                Instant global controls to pause mobile operations during critical database maintenance or force mobile client updates.
              </p>

              <div className="space-y-4 text-xs pt-2">
                <div className="p-4 rounded-2xl bg-[#10231d] dark:bg-[#10231d] border border-[#22493c] flex items-center justify-between">
                  <div>
                    <span className="font-bold text-white dark:text-white light:text-[#0D1C17] block">
                      App Maintenance Mode (Kill-Switch)
                    </span>
                    <span className="text-[11px] text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                      When activated, non-admin mobile users see the maintenance screen.
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={() => setGovernance({ ...governance, maintenanceMode: !governance.maintenanceMode })}
                    className={`w-12 h-6 rounded-full transition-colors relative ${governance.maintenanceMode ? 'bg-rose-500' : 'bg-[#22493c]'}`}
                  >
                    <div className={`w-5 h-5 rounded-full bg-white transition-transform ${governance.maintenanceMode ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </button>
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1">
                    Maintenance Announcement Broadcast Message:
                  </label>
                  <textarea
                    rows={2}
                    value={governance.maintenanceMessage}
                    onChange={(e) => setGovernance({ ...governance, maintenanceMessage: e.target.value })}
                    className="w-full p-2.5 rounded-xl text-xs outline-none border transition-colors
                      bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                      light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17] focus:border-brand-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1">
                    Minimum Supported Mobile App Version (Force Update):
                  </label>
                  <input
                    type="text"
                    value={governance.minSupportedAppVersion}
                    onChange={(e) => setGovernance({ ...governance, minSupportedAppVersion: e.target.value })}
                    className="w-full p-2.5 rounded-xl text-xs font-mono outline-none border transition-colors
                      bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                      light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17] focus:border-brand-500"
                  />
                  <span className="text-[10px] text-[#90cbb7]/70">Clients below this semantic version will be required to update via Google Play / App Store.</span>
                </div>
              </div>
            </div>

            {/* Viral Growth & Referral Rewards Engine */}
            <div className="rounded-3xl p-6 sm:p-8 border transition-colors space-y-4
              bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c]
              light:bg-white light:border-[#D5E4DC] light:shadow-sm
            ">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-xl bg-amber-500/15 text-amber-400">
                    <Gift className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-white dark:text-white light:text-[#0D1C17]">
                      Viral Referral Program Engine
                    </h4>
                    <span className="text-[11px] text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                      Rewards disbursed on first completed escrow trade
                    </span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setGovernance({ ...governance, referralProgramActive: !governance.referralProgramActive })}
                  className={`w-12 h-6 rounded-full transition-colors relative ${governance.referralProgramActive ? 'bg-brand-500' : 'bg-[#22493c]'}`}
                >
                  <div className={`w-5 h-5 rounded-full bg-white transition-transform ${governance.referralProgramActive ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>

              <div className="space-y-4 text-xs pt-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1">
                      Referrer Cash Reward (₦) on 1st Escrow:
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        min="0"
                        step="50"
                        value={governance.referralRewardNgn}
                        onChange={(e) => setGovernance({ ...governance, referralRewardNgn: Number(e.target.value) })}
                        className="w-full p-2.5 rounded-xl text-xs outline-none border transition-colors
                          bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                          light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17] focus:border-brand-500"
                      />
                      <span className="absolute right-3 top-2.5 text-[#90cbb7] font-semibold">NGN</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1">
                      Referee Welcome Discount (₦):
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        min="0"
                        step="50"
                        value={governance.refereeDiscountNgn}
                        onChange={(e) => setGovernance({ ...governance, refereeDiscountNgn: Number(e.target.value) })}
                        className="w-full p-2.5 rounded-xl text-xs outline-none border transition-colors
                          bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                          light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17] focus:border-brand-500"
                      />
                      <span className="absolute right-3 top-2.5 text-[#90cbb7] font-semibold">NGN</span>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1">
                    Eco-Points Awarded per Successful Referral:
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="10"
                    value={governance.referralEcoPoints}
                    onChange={(e) => setGovernance({ ...governance, referralEcoPoints: Number(e.target.value) })}
                    className="w-full p-2.5 rounded-xl text-xs outline-none border transition-colors
                      bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                      light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17] focus:border-brand-500"
                  />
                  <span className="text-[10px] text-[#90cbb7]/70">
                    Eco-Points boost vendor trust scores and unlock platform badges.
                  </span>
                </div>
              </div>
            </div>

            {/* Automated AI Content Moderation & Contraband Filter */}
            <div className="rounded-3xl p-6 sm:p-8 border transition-colors space-y-4
              bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c]
              light:bg-white light:border-[#D5E4DC] light:shadow-sm
            ">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-xl bg-purple-500/15 text-purple-400">
                    <Bot className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-white dark:text-white light:text-[#0D1C17]">
                      Automated AI Content Moderation
                    </h4>
                    <span className="text-[11px] text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                      Google Vision & Gemini automated image screening
                    </span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setGovernance({ ...governance, aiContentModerationEnabled: !governance.aiContentModerationEnabled })}
                  className={`w-12 h-6 rounded-full transition-colors relative ${governance.aiContentModerationEnabled ? 'bg-purple-500' : 'bg-[#22493c]'}`}
                >
                  <div className={`w-5 h-5 rounded-full bg-white transition-transform ${governance.aiContentModerationEnabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>

              <div className="space-y-4 text-xs pt-2">
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <label className="text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                      Auto-Quarantine Confidence Sensitivity:
                    </label>
                    <span className="font-bold text-white bg-[#10231d] px-2 py-0.5 rounded border border-[#22493c]">
                      {governance.aiConfidenceQuarantineThreshold}%
                    </span>
                  </div>
                  <input
                    type="range"
                    min="50"
                    max="95"
                    step="5"
                    value={governance.aiConfidenceQuarantineThreshold}
                    onChange={(e) => setGovernance({ ...governance, aiConfidenceQuarantineThreshold: Number(e.target.value) })}
                    className="w-full accent-[#0df2a6] cursor-pointer"
                  />
                  <span className="text-[10px] text-[#90cbb7]/70">
                    Listings exceeding this flag confidence are automatically quarantined to the Flagged Queue before appearing in the mobile app feed.
                  </span>
                </div>

                <div className="flex items-center justify-between p-3 rounded-xl bg-[#10231d] border border-[#22493c]">
                  <div>
                    <span className="font-bold text-white block">Auto-Flag Replica & Counterfeit Keywords</span>
                    <span className="text-[10px] text-[#90cbb7]">Filters phrases like "1:1 clone", "first copy", "replica"</span>
                  </div>
                  <input
                    type="checkbox"
                    checked={governance.autoFlagReplicaKeywords}
                    onChange={(e) => setGovernance({ ...governance, autoFlagReplicaKeywords: e.target.checked })}
                    className="w-4 h-4 accent-[#0df2a6] rounded cursor-pointer"
                  />
                </div>
              </div>
            </div>

            {/* Treasury Resilience & Payout Dead-Letter Queue (DLQ) */}
            <div className="rounded-3xl p-6 sm:p-8 border transition-colors space-y-4
              bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c]
              light:bg-white light:border-[#D5E4DC] light:shadow-sm
            ">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-xl bg-emerald-500/15 text-emerald-400">
                    <Activity className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-white dark:text-white light:text-[#0D1C17]">
                      Treasury Float & Payout DLQ
                    </h4>
                    <span className="text-[11px] text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                      Flutterwave float monitoring & failed payout auto-retry
                    </span>
                  </div>
                </div>

                {governance.failedPayoutCount > 0 ? (
                  <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-rose-500/20 text-rose-300 border border-rose-500/30">
                    {governance.failedPayoutCount} Failed Payouts
                  </span>
                ) : (
                  <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                    Treasury Healthy
                  </span>
                )}
              </div>

              <div className="space-y-4 text-xs pt-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1">
                      Low-Balance Alert Threshold (₦):
                    </label>
                    <input
                      type="number"
                      value={governance.flutterwaveFloatThreshold}
                      onChange={(e) => setGovernance({ ...governance, flutterwaveFloatThreshold: Number(e.target.value) })}
                      className="w-full p-2.5 rounded-xl text-xs outline-none border transition-colors
                        bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                        light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17] focus:border-brand-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1">
                      Emergency Alert Phone (SMS):
                    </label>
                    <input
                      type="text"
                      value={governance.lowBalanceAlertPhone}
                      onChange={(e) => setGovernance({ ...governance, lowBalanceAlertPhone: e.target.value })}
                      className="w-full p-2.5 rounded-xl text-xs outline-none border transition-colors
                        bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                        light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17] focus:border-brand-500"
                    />
                  </div>
                </div>

                {/* Dead-Letter Queue Auto-Retry Bar */}
                <div className="p-4 rounded-2xl bg-[#10231d] border border-[#22493c] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <div>
                    <span className="font-bold text-white block">Failed Payout Dead-Letter Queue</span>
                    <span className="text-[11px] text-[#90cbb7]">
                      {governance.failedPayoutCount > 0
                        ? `${governance.failedPayoutCount} vendor bank transfers queued due to recipient bank switch downtime.`
                        : 'All escrow vendor payouts have been settled with zero pending failures.'}
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={handleRetryFailedPayouts}
                    disabled={retryingPayouts || governance.failedPayoutCount === 0}
                    className="px-4 py-2 rounded-full text-xs font-bold text-forest-void bg-brand-500 hover:bg-brand-400 disabled:opacity-40 flex items-center gap-1.5 shrink-0 shadow-glow-sm"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${retryingPayouts ? 'animate-spin' : ''}`} />
                    <span>{retryingPayouts ? 'Retrying...' : 'Auto-Retry Failed Payouts'}</span>
                  </button>
                </div>

                {payoutsRetriedSuccess && (
                  <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-xs flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    Flutterwave Transfer API re-executed successfully. All queued vendor wallets credited.
                  </div>
                )}
              </div>
            </div>

            {/* Fraud Velocity & Security Anomaly Limits */}
            <div className="rounded-3xl p-6 sm:p-8 border transition-colors space-y-4
              bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c]
              light:bg-white light:border-[#D5E4DC] light:shadow-sm
            ">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-2 rounded-xl bg-rose-500/15 text-rose-400">
                  <ShieldAlert className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="text-base font-bold text-white dark:text-white light:text-[#0D1C17]">
                    Fraud Velocity & Anomaly Engine
                  </h4>
                  <span className="text-[11px] text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                    Automated rate limiting and escrow protection rules
                  </span>
                </div>
              </div>

              <div className="space-y-4 text-xs pt-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1">
                      Max Listings Allowed per Hour (per User):
                    </label>
                    <input
                      type="number"
                      min="5"
                      max="100"
                      value={governance.maxListingsPerHourPerUser}
                      onChange={(e) => setGovernance({ ...governance, maxListingsPerHourPerUser: Number(e.target.value) })}
                      className="w-full p-2.5 rounded-xl text-xs outline-none border transition-colors
                        bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                        light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17] focus:border-brand-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1">
                      Bank Change Withdrawal Lock Window:
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        min="0"
                        max="72"
                        value={governance.bankChangeWithdrawalFreezeHours}
                        onChange={(e) => setGovernance({ ...governance, bankChangeWithdrawalFreezeHours: Number(e.target.value) })}
                        className="w-full p-2.5 rounded-xl text-xs outline-none border transition-colors
                          bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                          light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17] focus:border-brand-500"
                      />
                      <span className="absolute right-3 top-2.5 text-[#90cbb7]">Hours</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 rounded-xl bg-[#10231d] border border-[#22493c]">
                  <div>
                    <span className="font-bold text-white block">GPS Geofence Spoofing Detection</span>
                    <span className="text-[10px] text-[#90cbb7]">Flags users whose listing coordinates jump &gt;500km within 1 hour</span>
                  </div>
                  <input
                    type="checkbox"
                    checked={governance.geofenceSpoofingCheckEnabled}
                    onChange={(e) => setGovernance({ ...governance, geofenceSpoofingCheckEnabled: e.target.checked })}
                    className="w-4 h-4 accent-[#0df2a6] rounded cursor-pointer"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t border-[#22493c]">
            <button
              type="submit"
              className="px-6 py-2.5 rounded-full text-xs font-bold text-forest-void bg-brand-500 hover:bg-brand-400 shadow-glow-sm transition-all"
            >
              Deploy Governance Parameters Live
            </button>
          </div>
        </form>
      )}

      {/* ADD ADMIN MODAL */}
      {addAdminModalOpen && (
        <Modal
          isOpen={addAdminModalOpen}
          onClose={() => setAddAdminModalOpen(false)}
          title="Onboard New Administrator"
          subtitle="Generate secure login credentials and assign designated platform permissions"
          maxWidth="md"
        >
          {createdAdminReceipt ? (
            /* Success Receipt Display */
            <div className="space-y-4 text-xs">
              <div className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-300">
                <h5 className="font-bold text-sm mb-1 flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" /> Admin Onboarded Successfully!
                </h5>
                <p className="text-[11px] leading-relaxed">
                  Provide these credentials to the staff member. They will be prompted to change their temporary password and enable 2FA on first login.
                </p>
              </div>

              <div className="p-4 rounded-2xl bg-[#10231d] dark:bg-[#10231d] border border-[#22493c] space-y-2.5">
                <div className="flex justify-between items-center">
                  <span className="text-[#90cbb7]">Portal URL:</span>
                  <span className="font-bold text-white font-mono">http://localhost:3000/</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#90cbb7]">Email Address:</span>
                  <span className="font-bold text-white">{createdAdminReceipt.email}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#90cbb7]">Designated Role:</span>
                  <span className="font-bold text-brand-400 capitalize">{createdAdminReceipt.role.replace('_', ' ')}</span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-[#22493c]">
                  <span className="text-[#90cbb7]">Generated Password:</span>
                  <span className="font-mono font-bold text-emerald-300 tracking-wider bg-[#152a23] px-2 py-0.5 rounded">
                    {createdAdminReceipt.pass}
                  </span>
                </div>
              </div>

              <div className="pt-4 border-t border-[#22493c] flex justify-between items-center">
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(`ReUseIt Admin Portal: http://localhost:3000/\nEmail: ${createdAdminReceipt.email}\nTemporary Password: ${createdAdminReceipt.pass}\nRole: ${createdAdminReceipt.role}`);
                    setCopiedCredential(true);
                    setTimeout(() => setCopiedCredential(false), 2500);
                  }}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold text-brand-400 bg-brand-500/10 hover:bg-brand-500/20 border border-brand-500/20"
                >
                  {copiedCredential ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  <span>{copiedCredential ? 'Copied to Clipboard' : 'Copy Credentials'}</span>
                </button>

                <button
                  type="button"
                  onClick={() => setAddAdminModalOpen(false)}
                  className="px-5 py-2 rounded-full text-xs font-bold text-forest-void bg-brand-500 hover:bg-brand-400"
                >
                  Done
                </button>
              </div>
            </div>
          ) : (
            /* Creation Form */
            <div className="space-y-4 text-xs">
              <div>
                <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1">
                  Full Name:
                </label>
                <input
                  type="text"
                  value={newAdminName}
                  onChange={(e) => setNewAdminName(e.target.value)}
                  placeholder="e.g. Chukwuma Obi"
                  className="w-full p-2.5 rounded-xl text-xs outline-none border transition-colors
                    bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                    light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17] focus:border-brand-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1">
                  Official Email Address:
                </label>
                <input
                  type="email"
                  value={newAdminEmail}
                  onChange={(e) => setNewAdminEmail(e.target.value)}
                  placeholder="e.g. chukwuma.obi@reuseit.shop"
                  className="w-full p-2.5 rounded-xl text-xs outline-none border transition-colors
                    bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                    light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17] focus:border-brand-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1">
                  Designated Role:
                </label>
                <select
                  value={newAdminRole}
                  onChange={(e) => setNewAdminRole(e.target.value as AdminRole)}
                  className="w-full p-2.5 rounded-xl text-xs outline-none border transition-colors
                    bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                    light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]"
                >
                  <option value="super_admin">Super Admin (Full Jurisdiction & Overrides)</option>
                  <option value="mediator">Dispute Mediator (Tribunal & Evidence Rulings)</option>
                  <option value="content_editor">Content Editor (CMS & Community Events)</option>
                  <option value="support_agent">Support Agent (Inbound User Helpdesk)</option>
                </select>
              </div>

              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                    Generated High-Security Password:
                  </label>
                  <button
                    type="button"
                    onClick={() => setGeneratedPassword(generateSecurePassword())}
                    className="text-[11px] text-brand-400 hover:underline flex items-center gap-1"
                  >
                    <RotateCcw className="w-3 h-3" />
                    <span>Regenerate</span>
                  </button>
                </div>
                <div className="p-2.5 rounded-xl bg-[#10231d] border border-[#22493c] font-mono text-xs font-bold text-emerald-400">
                  {generatedPassword}
                </div>
              </div>

              <div className="pt-4 border-t border-[#22493c] flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setAddAdminModalOpen(false)}
                  className="px-4 py-2 rounded-full text-xs font-bold text-white bg-[#22493c] hover:bg-[#2a5547]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCreateAdmin}
                  disabled={!newAdminName.trim() || !newAdminEmail.trim()}
                  className="px-5 py-2 rounded-full text-xs font-bold text-forest-void bg-brand-500 hover:bg-brand-400 shadow-glow-sm disabled:opacity-50"
                >
                  Onboard Admin
                </button>
              </div>
            </div>
          )}
        </Modal>
      )}

      {/* RESET CREDENTIALS MODAL */}
      {resetModalAdmin && (
        <Modal
          isOpen={Boolean(resetModalAdmin)}
          onClose={() => setResetModalAdmin(null)}
          title={`Reset Credentials: ${resetModalAdmin.displayName}`}
          subtitle={`Generate new temporary login password for ${resetModalAdmin.email}`}
          maxWidth="md"
        >
          <div className="space-y-4 text-xs">
            <p className="text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] leading-relaxed">
              Generating new credentials will invalidate any previous temporary password for this administrator.
            </p>

            <div className="p-3.5 rounded-2xl bg-[#10231d] border border-[#22493c] space-y-2">
              <span className="text-[10px] text-[#90cbb7] uppercase font-bold">New Generated Password:</span>
              <div className="font-mono text-sm font-bold text-emerald-400 bg-[#152a23] p-2 rounded-xl border border-[#22493c] flex items-center justify-between">
                <span>{resetPasswordGenerated}</span>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(resetPasswordGenerated);
                    setResetCopied(true);
                    setTimeout(() => setResetCopied(false), 2500);
                  }}
                  className="text-xs text-brand-400 flex items-center gap-1 font-sans"
                >
                  {resetCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  <span>{resetCopied ? 'Copied' : 'Copy'}</span>
                </button>
              </div>
            </div>

            <div className="pt-4 border-t border-[#22493c] flex justify-between items-center">
              <button
                type="button"
                onClick={() => setResetModalAdmin(null)}
                className="px-4 py-2 rounded-full text-xs font-bold text-white bg-[#22493c]"
              >
                Close
              </button>

              <button
                type="button"
                onClick={() => {
                  handleConfirmResetCredentials();
                  setResetModalAdmin(null);
                }}
                className="px-5 py-2 rounded-full text-xs font-bold text-forest-void bg-brand-500 hover:bg-brand-400 shadow-glow-sm"
              >
                Apply & Save New Password
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* EDIT ADMIN & CHANGE LOGIN EMAIL MODAL */}
      {editAdminModalOpen && editingAdmin && (
        <Modal
          isOpen={editAdminModalOpen}
          onClose={() => setEditAdminModalOpen(false)}
          title={`Edit Administrator: ${editingAdmin.name}`}
          subtitle="Super Admin Jurisdiction: Update staff identity and official login email address"
          maxWidth="md"
        >
          <form onSubmit={handleSaveEditAdmin} className="space-y-4 text-xs">
            <div className="p-3.5 rounded-2xl bg-brand-500/10 border border-brand-500/25 text-brand-300 flex items-start gap-2.5">
              <ShieldCheck className="w-4 h-4 text-brand-400 shrink-0 mt-0.5" />
              <div className="leading-relaxed">
                <span className="font-bold block text-brand-200">Official Login Identifier Update</span>
                <span>
                  Modifying this email address changes the sign-in credential this administrator must use to log in across the web portal.
                </span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1.5">
                Full Staff Display Name
              </label>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                required
                placeholder="e.g. Tola Adebayo"
                className="w-full px-3.5 py-2.5 rounded-xl text-xs outline-none border transition-colors
                  bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                  light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]
                  focus:border-brand-500"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1.5">
                Official Admin Login Email Address
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[#90cbb7]">
                  <Mail className="w-4 h-4" />
                </div>
                <input
                  type="email"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                  required
                  placeholder="e.g. tola.adebayo@reuseit.shop"
                  className="w-full pl-9 pr-3.5 py-2.5 rounded-xl text-xs outline-none border transition-colors font-medium
                    bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                    light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]
                    focus:border-brand-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1.5">
                  Assigned Authority Role
                </label>
                <select
                  value={editRole}
                  onChange={(e) => setEditRole(e.target.value as AdminRole)}
                  className="w-full px-3.5 py-2.5 rounded-xl text-xs outline-none border transition-colors
                    bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                    light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]
                    focus:border-brand-500"
                >
                  <option value="super_admin">Super Admin (Unrestricted)</option>
                  <option value="mediator">Dispute Mediator (Escrow Tribunal)</option>
                  <option value="content_editor">Content Editor (CMS & News)</option>
                  <option value="support_agent">Support Agent (Triage & Desk)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1.5">
                  Account Status
                </label>
                <select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value as 'active' | 'suspended')}
                  className="w-full px-3.5 py-2.5 rounded-xl text-xs outline-none border transition-colors
                    bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                    light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]
                    focus:border-brand-500"
                >
                  <option value="active">Active (Access Allowed)</option>
                  <option value="suspended">Suspended (Access Revoked)</option>
                </select>
              </div>
            </div>

            <div className="p-3 rounded-xl bg-[#10231d] border border-[#22493c] flex items-center justify-between">
              <div>
                <span className="font-bold text-white block">Enforce 2FA Authenticator</span>
                <span className="text-[10px] text-[#90cbb7]">Requires TOTP app or email verification on sign in</span>
              </div>
              <input
                type="checkbox"
                checked={edit2fa}
                onChange={(e) => setEdit2fa(e.target.checked)}
                className="w-4 h-4 rounded text-brand-500 focus:ring-brand-500"
              />
            </div>

            <div className="pt-2 flex items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={() => setEditAdminModalOpen(false)}
                className="px-4 py-2.5 rounded-xl border border-[#22493c] text-xs font-semibold text-[#90cbb7] hover:text-white"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2.5 rounded-xl bg-brand-500 hover:bg-brand-400 text-forest-void font-extrabold text-xs shadow-glow-sm transition-all flex items-center gap-1.5"
              >
                <Check className="w-4 h-4 stroke-[3]" />
                <span>Save Changes & Update Email</span>
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};
